// api Edge Function 调用封装 · 自动附带用户 JWT
import { supabase } from "./supabase";
import type { EditorSlide, Layout, SlideContent } from "./slides";
import { ApiError, businessApiBase, requestJson } from "./http-client";
export { ApiError } from "./http-client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const API_BASE = businessApiBase(import.meta.env.VITE_API_BASE, SUPABASE_URL, import.meta.env.PROD);

/**
 * 本地开发时 Supabase Storage 签名 URL 使用容器内部主机名 kong:8000，
 * 浏览器无法解析，需替换为宿主机可达地址。
 */
function fixLocalUrl(url: string | null): string | null {
  if (!url) return url;
  return url.replace(/https?:\/\/kong:\d+/, SUPABASE_URL);
}

/** 带状态码与响应体的错误（409 冲突等场景需要结构化信息） */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJson<T>(`${API_BASE}${path}`, async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session?.access_token ?? "";
  }, init, /\/(?:export|render|rebuild)$/.test(path) ? 120_000 : 30_000);
}

// ---------- 类型 ----------

export interface SlideRow {
  id: string;
  idx: number;
  layout: Layout;
  content: SlideContent;
  updated_at?: string;
}

export interface DeckInfo {
  deck: { id: string; title: string; pptx_path?: string | null; style?: string | null; theme_id?: string | null; job_id?: string | null };
  slides: SlideRow[];
  downloadUrl: string | null;
  /** 建议下载文件名（后端已做 Windows/Office 安全处理） */
  downloadName: string | null;
  maxUpdatedAt: string | null;
}

export interface DeckImage {
  id: string; path: string; url: string; filename: string; created_at: string;
}

/** 工作台项目卡片（GET /projects 嵌套 jobs/decks） */
export interface ProjectListItem {
  id: string;
  name: string;
  industry: string | null;
  created_at: string;
  updated_at: string;
  jobs: { id: string; status: string; progress: number | null; created_at: string; quality_status?: string | null }[];
  decks: { id: string; created_at: string; style?: string | null; theme_id?: string | null; cover?: SlideContent | null }[];
}

export interface DeckVersion {
  id: string; version: number; note: string | null; created_at: string;
}


/** 成本看板响应（M4.3） */
export interface AdminStats {
  days: number;
  daily: { date: string; jobs: number; done: number; failed: number; canceled: number; running: number; tokens: number; costCents: number; llmCalls: number }[];
  totals: { jobs: number; done: number; failed: number; tokens: number; costCents: number; llmCalls: number; degraded: number };
  models: { model: string; calls: number; tokens: number; costCents: number }[];
  topUsers: { userId: string; name: string; jobs: number; costCents: number }[];
  alert: { thresholdCents: number; todayCostCents: number; over: boolean };
}

// ==================== 099 运营后台（/admin/*，服务端 ADMIN_EMAILS 授权） ====================
// 口径约定与 Docs/099 ToDoList §4 一致：null = 未接入/未采集，前端诚实展示不编造。

export interface OpsRange {
  days: number; since: string; until: string; timezone: string; updatedAt: string;
}

export interface OpsOverview {
  range: OpsRange;
  sources: { analytics: null }; // GA4 未接入
  kpis: {
    visitors: number | null; pageviews: number | null;
    signups: number; totalUsers: number; activeUsers: number; newProjects: number;
    jobsStarted: number; done: number; failed: number; canceled: number; runningNow: number;
    tokensIn: number; tokensOut: number; costCents: number;
  };
  prev: {
    signups: number; activeUsers: number; newProjects: number; jobsStarted: number; done: number; costCents: number;
    pct: { signups: number | null; activeUsers: number | null; jobsStarted: number | null; done: number | null; costCents: number | null };
  };
  daily: { date: string; signups: number; jobs: number; done: number; costCents: number }[];
  ttfv: {
    sample: number;
    regToFirstProjectMs: { median: number | null; p95: number | null };
    firstProjectToFirstDeckMs: { median: number | null; p95: number | null };
    totalMs: { median: number | null; p95: number | null };
  };
  events: { id: string; event_date: string; note: string }[];
}

export interface OpsUserRow {
  id: string; email: string; displayName: string | null;
  createdAt: string; confirmed: boolean; unverifiedHours: number; isOps: boolean;
  projects: number; jobs: number; done: number; failed: number;
  tokens: number; costCents: number; lastActivity: string | null;
}

export interface OpsUsers {
  range: OpsRange;
  summary: { total: number; verifiedRate: number | null; unverified48h: number; opsAccounts: number };
  users: OpsUserRow[];
}

export interface OpsProjectRow {
  id: string; name: string; industry: string | null;
  createdAt: string; updatedAt: string;
  ownerId: string; ownerEmail: string | null; ownerName: string | null;
  jobsCount: number; rounds: number;
  latestStatus: string | null; latestQuality: string | null; latestDegraded: boolean; latestJobAt: string | null;
  deckCount: number; hasPptx: boolean;
  statusBucket: "done" | "failed" | "canceled" | "running" | "none";
}

export interface OpsProjects {
  range: OpsRange; total: number; page: number; pageSize: number;
  industries: string[]; projects: OpsProjectRow[];
}

export interface OpsProjectDetail {
  project: { id: string; name: string; industry: string | null; form: Record<string, unknown>; factSheet: unknown; createdAt: string; updatedAt: string };
  owner: { id: string; email: string | null; displayName: string | null };
  assets: { id: string; kind: string; filename: string | null; meta: Record<string, unknown>; createdAt: string }[];
  jobs: {
    id: string; status: string; progress: number; degraded: boolean; qualityStatus: string | null;
    error: string; llmCalls: number; tokensTotal: number; costCents: number; pipeline: string;
    createdAt: string; updatedAt: string; finishedAt: string | null;
  }[];
  decks: { id: string; jobId: string | null; title: string; style: string | null; themeId: string | null; hasPptx: boolean; createdAt: string }[];
  preview: { deckId: string; title: string; slides: { idx: number; layout: Layout; content: SlideContent }[] } | null;
}

export interface OpsJobDetail {
  job: {
    id: string; status: string; progress: number; degraded: boolean; qualityStatus: string | null;
    error: string; llmCalls: number; tokensTotal: number; costCents: number; pipeline: string;
    createdAt: string; updatedAt: string; finishedAt: string | null;
  };
  project: { id: string; name: string } | null;
  ownerEmail: string | null;
  running: { currentStep: string; lastHeartbeat: string; stuckMs: number } | null;
  steps: {
    step: string; agent: string; status: string; model: string | null;
    duration_ms: number | null; error: string; tokens_in: number | null; tokens_out: number | null;
    started_at: string | null; finished_at: string | null;
  }[];
}

export interface OpsGenHealth {
  range: OpsRange;
  status: { done: number; failed: number; canceled: number; runningNow: number; total: number };
  /** null = 质量认证字段未上云，未采集（harness v2 未发布） */
  quality: { certified: number; needs_revision: number; human_review: number; unreviewed: number; doneTotal: number } | null;
  durationMs: { sample: number; median: number | null; p95: number | null };
  queueWaitMs: { sample: number; median: number | null };
  agents: { agent: string; failed: number; runs: number; retried: number }[];
  degraded: number;
  models: { model: string; calls: number; tokensIn: number; tokensOut: number; costCents: number }[];
  image: { count: number; costCents: number; estimated: boolean };
  costCrossCheck: { jobsCostCents: number; llmCallsCostCents: number };
  topJobs: { id: string; projectId: string; projectName: string | null; status: string; qualityStatus: string | null; tokensTotal: number; costCents: number }[];
  alert: { thresholdCents: number; todayCostCents: number; over: boolean };
  regeneration: { projectsWithJobs: number; multiRound: number; multiRoundRate: number | null; avgRounds: number | null; note: string };
}

export interface OpsFunnel {
  range: OpsRange;
  cohortSize: number;
  stages: { key: string; label: string; count: number | null; source: string | null; note?: string }[];
  gapMedianMs: { regToProj: number | null; projToJob: number | null; jobToDone: number | null };
}

export interface OpsExceptionItem {
  jobId: string; projectId: string; projectName: string | null; ownerEmail: string | null;
  time: string; type: "failed" | "stuck" | "degraded" | "needs_revision";
  severity: "high" | "mid"; step: string | null; summary: string;
}

export interface OpsExceptions {
  range: OpsRange;
  statusTracking: { collected: false; note: string };
  feedback: { collected: false; note: string };
  satisfaction: { collected: false; note: string };
  budget: { thresholdCents: number };
  items: OpsExceptionItem[];
}

export interface OpsTraffic {
  range: OpsRange;
  analytics: { connected: false; provider: null; note: string };
  businessDaily: { date: string; signups: number; projects: number; jobs: number; done: number }[];
  events: { key: string; name: string; source: string | null; collected: boolean; count: number | null }[];
}

export interface OpsSiteHealth {
  range: OpsRange;
  configured: false;
  sources: { sentry: boolean; cloudflare: boolean };
  note: string;
}

export interface OpsEvent {
  id: string; event_date: string; note: string; created_at: string;
}

/** 运营后台接口组（099 D01–D13）。403 = 非授权运营；前端 Layout 据此整体拦截。
 *  走独立的 ops-api 函数（与业务 api 分离部署，云端 api 版本较旧不受本次发布影响）。 */
const OPS_BASE = `${SUPABASE_URL}/functions/v1/ops-api`;

async function opsRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? "";
  const res = await fetch(`${OPS_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError((body as { error?: string }).error ?? `HTTP ${res.status}`, res.status, body);
  }
  return body as T;
}

export const ops = {
  me: () => opsRequest<{ ok: boolean; email: string }>("/admin/me"),
  overview: (days = 7) => opsRequest<OpsOverview>(`/admin/overview?days=${days}`),
  users: () => opsRequest<OpsUsers>("/admin/users"),
  projects: (params: { status?: string; industry?: string; q?: string; page?: number; pageSize?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.status) sp.set("status", params.status);
    if (params.industry) sp.set("industry", params.industry);
    if (params.q) sp.set("q", params.q);
    if (params.page) sp.set("page", String(params.page));
    if (params.pageSize) sp.set("pageSize", String(params.pageSize));
    const qs = sp.toString();
    return opsRequest<OpsProjects>(`/admin/projects${qs ? `?${qs}` : ""}`);
  },
  project: (id: string) =>
    opsRequest<OpsProjectDetail>(`/admin/projects/${id}`)
      .then((r) => ({
        ...r,
        preview: r.preview
          ? {
              ...r.preview,
              slides: r.preview.slides.map((s) => ({
                ...s,
                content: {
                  ...s.content,
                  blocks: (s.content.blocks ?? []).map((b) =>
                    b.type === "image" ? { ...b, url: fixLocalUrl(b.url ?? null) ?? undefined } : b),
                },
              })),
            }
          : null,
      })),
  projectDeckDownload: (projectId: string, deckId: string) =>
    opsRequest<{ url: string; name: string; expiresIn: number }>(`/admin/projects/${projectId}/decks/${deckId}/download`)
      .then((r) => ({ ...r, url: fixLocalUrl(r.url)! })),
  job: (id: string) => opsRequest<OpsJobDetail>(`/admin/jobs/${id}`),
  genHealth: (days = 7) => opsRequest<OpsGenHealth>(`/admin/gen-health?days=${days}`),
  funnel: (days = 7) => opsRequest<OpsFunnel>(`/admin/funnel?days=${days}`),
  exceptions: (days = 7) => opsRequest<OpsExceptions>(`/admin/exceptions?days=${days}`),
  traffic: (days = 7) => opsRequest<OpsTraffic>(`/admin/traffic?days=${days}`),
  siteHealth: () => opsRequest<OpsSiteHealth>("/admin/site-health"),
  addEvent: (date: string, note: string) =>
    opsRequest<{ event: OpsEvent }>("/admin/ops-events", { method: "POST", body: JSON.stringify({ date, note }) }),
};

export const api = {
  createProject: (p: { name: string; industry?: string; form?: Record<string, unknown> }, requestId?: string) =>
    request<{ id: string }>("/projects", { method: "POST", body: JSON.stringify({ ...p, client_request_id: requestId }) }),
  listProjects: () => request<{ projects: ProjectListItem[] }>("/projects"),
  getProject: (id: string) => request<{ project: unknown }>(`/projects/${id}`),
  startJob: (projectId: string, requestId?: string) =>
    request<{ job_id: string }>(`/projects/${projectId}/jobs`, { method: "POST", body: JSON.stringify({ client_request_id: requestId }) }),
  getJob: (id: string) =>
    request<{
      status: string; progress: number; degraded: boolean; paused: boolean;
      pipeline: string;
      error: string | null; cost_cents: number; llm_calls: number; tokens_total: number;
      steps: {
        step: string; agent: string; status: string; model: string | null;
        duration_ms: number | null; error: string | null;
        output: Record<string, unknown> | null;
        citations: { title: string; url: string; snippet?: string; tag?: string }[];
        started_at: string | null; finished_at: string | null;
      }[];
    }>(`/jobs/${id}`),
  cancelJob: (id: string) => request<{ ok: boolean }>(`/jobs/${id}/cancel`, { method: "POST" }),
  retryJob: (id: string) => request<{ ok: boolean; resume: string }>(`/jobs/${id}/retry`, { method: "POST" }),
  // 048 F3：配图降级补出（done 任务：artwork 重跑 + assemble 重装）
  retryArtwork: (id: string) => request<{ ok: boolean }>(`/jobs/${id}/retry-artwork`, { method: "POST" }),
  pauseJob: (id: string) => request<{ ok: boolean }>(`/jobs/${id}/pause`, { method: "POST" }),
  resumeJob: (id: string) => request<{ ok: boolean }>(`/jobs/${id}/resume`, { method: "POST" }),
  sendFeedback: (id: string, text: string) =>
    request<{ ok: boolean }>(`/jobs/${id}/feedback`, { method: "POST", body: JSON.stringify({ text }) }),

  // ---- decks ----
  getDeck: (id: string) =>
    request<DeckInfo>(`/decks/${id}`)
      .then(r => ({
        ...r,
        downloadUrl: fixLocalUrl(r.downloadUrl),
        slides: r.slides.map(s => ({
          ...s,
          content: {
            ...s.content,
            blocks: (s.content.blocks ?? []).map(b =>
              b.type === "image" ? { ...b, url: fixLocalUrl(b.url ?? null) ?? undefined } : b),
          },
        })),
      })),

  /** 整册保存（任务 3.2）：409 时抛 ApiError(status=409, payload.serverUpdatedAt) */
  saveSlides: (id: string, slides: { layout: Layout; content: SlideContent }[], baseUpdatedAt: string | null) =>
    request<{ ok: boolean; count: number; serverUpdatedAt: string | null }>(
      `/decks/${id}/slides`,
      { method: "PUT", body: JSON.stringify({ slides, baseUpdatedAt }) },
    ),

  /** 申请资料上传签名 URL（1026 F3：用户文档 → 直传 inputs 桶 → intake 事实库） */
  createInputUpload: (projectId: string, filename: string) =>
    request<{ uploadUrl: string; path: string; token: string; assetId: string }>(
      `/projects/${projectId}/assets`, { method: "POST", body: JSON.stringify({ filename }) },
    ),

  /** 申请配图上传签名 URL（任务 3.3） */
  createImageUpload: (id: string, filename: string) =>
    request<{ uploadUrl: string; path: string; token: string; assetId: string }>(
      `/decks/${id}/images`, { method: "POST", body: JSON.stringify({ filename }) },
    ),

  /** 素材库（任务 3.3） */
  listImages: (id: string) =>
    request<{ images: DeckImage[] }>(`/decks/${id}/images`)
      .then(r => ({ images: r.images.map(i => ({ ...i, url: fixLocalUrl(i.url)! })) })),

  /** 版本快照（任务 3.5） */
  listVersions: (id: string) => request<{ versions: DeckVersion[] }>(`/decks/${id}/versions`),
  createVersion: (id: string, note?: string) =>
    request<{ ok: boolean; version: number }>(`/decks/${id}/versions`, { method: "POST", body: JSON.stringify({ note }) }),
  restoreVersion: (id: string, versionId: string) =>
    request<{ ok: boolean; restoredVersion: number; count: number; serverUpdatedAt: string | null }>(
      `/decks/${id}/versions/${versionId}/restore`, { method: "POST" },
    ),

  /** 成本看板（M4.3，管理员）：403 = 非管理员 */
  adminStats: (days = 14) =>
    request<AdminStats>(`/admin/stats?days=${days}`),

  /** 再导出（任务 3.5）：不传 versionId = 按当前 slides 重渲染；filename 为建议下载名 */
  exportDeck: (id: string, versionId?: string) =>
    request<{ url: string; filename: string }>(`/decks/${id}/export`, { method: "POST", body: JSON.stringify(versionId ? { versionId } : {}) })
      .then(r => ({ ...r, url: fixLocalUrl(r.url)! })),

  /** V2：整册换视觉主题（020 F7，编辑器右栏换肤） */
  updateDeckTheme: (id: string, themeId: string) =>
    request<{ ok: boolean; style: string; theme_id: string }>(`/decks/${id}/theme`, {
      method: "PATCH", body: JSON.stringify({ theme_id: themeId }),
    }),
};

/** 把 EditorSlide[] 转成保存载荷 */
export function toSavePayload(slides: EditorSlide[]): { layout: Layout; content: SlideContent }[] {
  return slides.map(s => ({ layout: s.layout, content: s.content }));
}
